/**
 * Test Inbox env configuration.
 *
 * Every non-prod env (dev, integration, staging) appears by default,
 * pointing at its production URL. Set VITE_TEST_INBOX_URL_<ENV> to
 * override the URL, or to "" to hide the env entirely. Prod is
 * intentionally absent from EnvName — there is no prod test-inbox
 * stack (per ADR-003), and a stray VITE_TEST_INBOX_URL_PROD must not
 * surface a tab.
 */

import type { EnvName as OpsEnvName } from "@/lib/env";

// Test Inbox exists only in non-prod (no prod stack, per ADR-003). This subset
// is `satisfies`-checked against the canonical ops env names, so removing one
// from @/lib/env surfaces an error here.
const ENV_ORDER = ["dev", "integration", "staging"] as const satisfies readonly OpsEnvName[];

export type EnvName = (typeof ENV_ORDER)[number];

export interface EnvConfig {
  env: EnvName;
  baseUrl: string;
}

export interface Stats {
  env: string;
  namespaceCount: number;
  messagesLast24h: number;
  latestReceivedAt: string | null;
}

export interface Health {
  status: "ok";
  service: string;
  time: string;
}

const ENV_VAR_BY_NAME: Record<EnvName, string> = {
  dev: "VITE_TEST_INBOX_URL_DEV",
  integration: "VITE_TEST_INBOX_URL_INTEGRATION",
  staging: "VITE_TEST_INBOX_URL_STAGING",
};

const DEFAULT_URL_BY_NAME: Record<EnvName, string> = {
  dev: "https://api.dev.letsendure.dev/test-inbox",
  integration: "https://api.integration.letsendure.dev/test-inbox",
  staging: "https://api.staging.letsendure.dev/test-inbox",
};

export function getConfiguredEnvs(): EnvConfig[] {
  const env = import.meta.env as Record<string, string | undefined>;
  const configs: EnvConfig[] = [];
  for (const name of ENV_ORDER) {
    const override = env[ENV_VAR_BY_NAME[name]];
    if (override === "") continue; // explicit disable
    const baseUrl = override ?? DEFAULT_URL_BY_NAME[name];
    configs.push({ env: name, baseUrl });
  }
  return configs;
}

export async function fetchHealth(baseUrl: string): Promise<Health | null> {
  try {
    const res = await fetch(`${baseUrl}/health`);
    if (!res.ok) return null;
    return (await res.json()) as Health;
  } catch {
    return null;
  }
}

export async function fetchStats(baseUrl: string): Promise<Stats | null> {
  try {
    const res = await fetch(`${baseUrl}/stats`);
    if (!res.ok) return null;
    return (await res.json()) as Stats;
  } catch {
    return null;
  }
}
